import React, { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Save,
  Globe,
  ArrowLeft,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Upload,
  Loader2,
  Image as ImageIcon,
  MessageSquare,
  Clock,
  X,
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import { INDUSTRIES, DEFAULT_HOURS } from '../../utils/industrySchemas'

// ─── Field renderers (identical to ContentEditor) ────────────────────────────

const TextField = ({ field, value, onChange }) => (
  <div>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }}>
      {field.label}
    </label>
    <input
      type={field.type === 'url' ? 'url' : 'text'}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder}
      className="ds-input w-full"
    />
  </div>
)

const TextAreaField = ({ field, value, onChange }) => (
  <div>
    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }}>
      {field.label}
    </label>
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={field.placeholder}
      rows={4}
      className="ds-input w-full"
      style={{ resize: 'vertical' }}
    />
  </div>
)

const ImageField = ({ field, value, onChange, clientId }) => {
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('files', file)
      if (clientId) form.append('clientId', clientId)
      const res = await apiService.post('/media/upload', form)
      onChange(res.data.files?.[0]?.public_url)
      toast.success('Image uploaded')
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }}>
        {field.label}
      </label>
      <div className="flex items-center gap-3">
        {value ? (
          <div style={{ position: 'relative', width: 80, height: 80, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)', flexShrink: 0 }}
            onMouseEnter={e => e.currentTarget.querySelector('.img-overlay').style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.querySelector('.img-overlay').style.opacity = '0'}
          >
            <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <button
              onClick={() => onChange('')}
              className="img-overlay"
              style={{
                position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)',
                opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', cursor: 'pointer', transition: 'opacity .15s',
              }}
            >
              <Trash2 size={16} style={{ color: 'white' }} />
            </button>
          </div>
        ) : (
          <div style={{
            width: 80, height: 80, borderRadius: 10, border: '2px dashed var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <ImageIcon size={24} style={{ color: 'var(--text-faint)' }} />
          </div>
        )}
        <label
          className="btn btn-ghost btn-sm"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', opacity: uploading ? 0.5 : 1, pointerEvents: uploading ? 'none' : 'auto' }}
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Uploading...' : 'Upload image'}
          <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    </div>
  )
}

const MultiImageField = ({ field, value = [], onChange, clientId }) => {
  const [uploading, setUploading] = useState(false)

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setUploading(true)
    try {
      const urls = await Promise.all(files.map(async (file) => {
        const form = new FormData()
        form.append('files', file)
        if (clientId) form.append('clientId', clientId)
        const res = await apiService.post('/media/upload', form)
        return res.data.files?.[0]?.public_url
      }))
      onChange([...value, ...urls])
      toast.success(`${urls.length} image(s) uploaded`)
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const removeImage = (index) => onChange(value.filter((_, i) => i !== index))

  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }}>
        {field.label}
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 12 }}>
        {value.map((url, i) => (
          <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}
            onMouseEnter={e => e.currentTarget.querySelector('.img-overlay').style.opacity = '1'}
            onMouseLeave={e => e.currentTarget.querySelector('.img-overlay').style.opacity = '0'}
          >
            <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <button
              onClick={() => removeImage(i)}
              className="img-overlay"
              style={{
                position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)',
                opacity: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', cursor: 'pointer', transition: 'opacity .15s',
              }}
            >
              <Trash2 size={14} style={{ color: 'white' }} />
            </button>
          </div>
        ))}
        <label style={{
          aspectRatio: '1', borderRadius: 10, border: '2px dashed var(--border)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'border-color .15s',
          opacity: uploading ? 0.5 : 1, pointerEvents: uploading ? 'none' : 'auto',
        }}>
          {uploading
            ? <Loader2 size={18} className="animate-spin" style={{ color: 'var(--text-faint)' }} />
            : <Plus size={18} style={{ color: 'var(--text-faint)' }} />}
          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    </div>
  )
}

const HoursField = ({ value, onChange }) => {
  const rows = Array.isArray(value) && value.length ? value : DEFAULT_HOURS
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  const timeStyle = (open) => ({ fontSize: 12.5, padding: '6px 9px', fontFamily: 'var(--font-mono)', opacity: open ? 1 : 0.4 })

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div className="vars-row vars-header" style={{ gridTemplateColumns: '1fr 1fr 1fr 90px' }}>
        <div>Day</div><div>Opens</div><div>Closes</div><div>Closed</div>
      </div>
      {rows.map((row, i) => (
        <div className="vars-row" key={row.day} style={{ gridTemplateColumns: '1fr 1fr 1fr 90px', alignItems: 'center' }}>
          <div style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text)' }}>
            <Clock size={13} style={{ color: 'var(--text-faint)', flexShrink: 0 }} />
            {row.day}
          </div>
          <div>
            <input
              type="time"
              className="input"
              value={row.openTime || ''}
              disabled={!row.open}
              onChange={e => update(i, { openTime: e.target.value })}
              style={timeStyle(row.open)}
            />
          </div>
          <div>
            <input
              type="time"
              className="input"
              value={row.closeTime || ''}
              disabled={!row.open}
              onChange={e => update(i, { closeTime: e.target.value })}
              style={timeStyle(row.open)}
            />
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <input type="checkbox" checked={!row.open} onChange={e => update(i, { open: !e.target.checked })} />
              Closed
            </label>
          </div>
        </div>
      ))}
    </div>
  )
}

const ListField = ({ fieldDef, value = [], onChange, clientId }) => {
  const [expanded, setExpanded] = useState(null)

  const addItem = () => {
    const newItem = {}
    fieldDef.subFields.forEach(sf => { newItem[sf.key] = sf.type === 'multi-image' ? [] : '' })
    const updated = [...value, newItem]
    onChange(updated)
    setExpanded(updated.length - 1)
  }

  const removeItem = (index) => {
    onChange(value.filter((_, i) => i !== index))
    if (expanded === index) setExpanded(null)
  }

  const updateItem = (index, key, val) => {
    const updated = [...value]
    updated[index] = { ...updated[index], [key]: val }
    onChange(updated)
  }

  return (
    <div className="col gap-2">
      {value.map((item, i) => (
        <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'var(--surface-2)' }}>
            <button
              type="button"
              onClick={() => setExpanded(expanded === i ? null : i)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 500, color: 'var(--text-soft)', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              {expanded === i ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {item[fieldDef.subFields[0]?.key] || `Item ${i + 1}`}
            </button>
            <button
              type="button"
              onClick={() => removeItem(i)}
              className="btn btn-ghost btn-icon btn-sm"
              style={{ color: 'var(--text-faint)' }}
            >
              <Trash2 size={14} />
            </button>
          </div>

          {expanded === i && (
            <div className="col gap-4 card-pad" style={{ background: 'var(--surface)' }}>
              {fieldDef.subFields.map(sf => (
                <div key={sf.key}>
                  {sf.type === 'text' || sf.type === 'url' ? (
                    <TextField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} />
                  ) : sf.type === 'textarea' ? (
                    <TextAreaField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} />
                  ) : sf.type === 'image' ? (
                    <ImageField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} clientId={clientId} />
                  ) : sf.type === 'multi-image' ? (
                    <MultiImageField field={sf} value={item[sf.key]} onChange={v => updateItem(i, sf.key, v)} clientId={clientId} />
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      <button type="button" onClick={addItem} className="btn btn-ghost w-full" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        <Plus size={14} />
        {fieldDef.addLabel || 'Add Item'}
      </button>
    </div>
  )
}

// ─── Menu field (categories + inline item table) ─────────────────────────────
// Backed by the existing flat `menu` list — each item keeps its
// { category, name, description, price, image } shape, so published sites keep
// rendering unchanged. Categories are derived from the items' `category` values;
// freshly-added empty categories live in local state until an item uses them.

const MENU_GRID = '56px 1.4fr 0.8fr 1fr 64px'

const MenuImageCell = ({ value, onChange, clientId }) => {
  const [uploading, setUploading] = useState(false)
  const handleUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('files', file)
      if (clientId) form.append('clientId', clientId)
      const res = await apiService.post('/media/upload', form)
      onChange(res.data.files?.[0]?.public_url || '')
    } catch {
      toast.error('Upload failed')
    } finally {
      setUploading(false)
    }
  }
  return (
    <label
      title={value ? 'Replace image' : 'Set image'}
      style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', overflow: 'hidden', flexShrink: 0 }}
    >
      {uploading
        ? <Loader2 size={13} className="animate-spin" style={{ color: 'var(--text-faint)' }} />
        : value
          ? <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <ImageIcon size={14} style={{ color: 'var(--text-faint)' }} />}
      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
    </label>
  )
}

const MenuField = ({ value, onChange, clientId }) => {
  const items = Array.isArray(value) ? value : []
  const [extraCats, setExtraCats] = useState([])
  const [newCat, setNewCat] = useState('')
  const [filter, setFilter] = useState('all')
  const [expanded, setExpanded] = useState(null)

  const catOf = (it) => (it.category || '').trim()
  const derived = []
  items.forEach(it => { const c = catOf(it); if (c && !derived.includes(c)) derived.push(c) })
  const categories = [...derived, ...extraCats.filter(c => !derived.includes(c))]

  const addCategory = () => {
    const name = newCat.trim()
    if (!name || categories.includes(name)) return
    setExtraCats([...extraCats, name]); setNewCat(''); setFilter(name)
  }
  const removeCategory = (name) => {
    const count = items.filter(it => catOf(it) === name).length
    if (count > 0 && !window.confirm(`Remove "${name}" and its ${count} item${count > 1 ? 's' : ''}?`)) return
    onChange(items.filter(it => catOf(it) !== name))
    setExtraCats(extraCats.filter(c => c !== name))
    if (filter === name) setFilter('all')
  }
  const renameCategory = (oldName, raw) => {
    const nn = (raw || '').trim()
    if (!nn || nn === oldName || categories.includes(nn)) return
    onChange(items.map(it => (catOf(it) === oldName ? { ...it, category: nn } : it)))
    setExtraCats(extraCats.map(c => (c === oldName ? nn : c)))
    if (filter === oldName) setFilter(nn)
  }
  const addItem = () => {
    const cat = filter !== 'all' ? filter : (categories[0] || '')
    onChange([...items, { category: cat, name: 'New item', description: '', price: '', image: '' }])
  }
  const updateItem = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx))

  const visibleCount = items.filter(it => filter === 'all' || catOf(it) === filter).length

  return (
    <div>
      {/* Categories manager */}
      <div className="card card-pad">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-soft)' }}>Categories</span>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>{categories.length} categories · {items.length} items</span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <button type="button" className={`section-tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')} style={{ borderRadius: 999 }}>
            All items
          </button>
          {categories.map(cat => (
            <span
              key={cat}
              className={`section-tab ${filter === cat ? 'active' : ''}`}
              style={{ borderRadius: 999, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
              onClick={() => setFilter(cat)}
            >
              <span
                contentEditable
                suppressContentEditableWarning
                onClick={e => e.stopPropagation()}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur() } }}
                onBlur={e => renameCategory(cat, e.currentTarget.textContent)}
                style={{ outline: 'none' }}
              >
                {cat}
              </span>
              <span style={{ fontSize: 10, color: 'var(--text-faint)' }}>{items.filter(it => catOf(it) === cat).length}</span>
              <button
                type="button"
                className="site-action"
                style={{ width: 16, height: 16, padding: 0 }}
                onClick={e => { e.stopPropagation(); removeCategory(cat) }}
                title="Remove category"
              >
                <X size={10} />
              </button>
            </span>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              className="input"
              placeholder="New category"
              value={newCat}
              onChange={e => setNewCat(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addCategory()}
              style={{ width: 130, fontSize: 12, padding: '6px 10px' }}
            />
            <button type="button" className="btn btn-sm btn-primary" onClick={addCategory}><Plus size={13} /> Add</button>
          </div>
        </div>
      </div>

      {/* Items table */}
      <div className="card" style={{ overflow: 'hidden', marginTop: 14 }}>
        <div className="vars-row vars-header" style={{ gridTemplateColumns: MENU_GRID }}>
          <div>Image</div><div>Name</div><div>Price</div><div>Category</div><div></div>
        </div>
        {visibleCount === 0 && (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 12.5, color: 'var(--text-faint)' }}>No items in this category yet.</div>
        )}
        {items.map((it, idx) => {
          if (filter !== 'all' && catOf(it) !== filter) return null
          return (
            <div key={idx} style={{ borderTop: '1px solid var(--border)' }}>
              <div className="vars-row" style={{ gridTemplateColumns: MENU_GRID, alignItems: 'center', borderTop: 'none' }}>
                <div><MenuImageCell value={it.image} onChange={v => updateItem(idx, { image: v })} clientId={clientId} /></div>
                <div><input className="input" value={it.name || ''} onChange={e => updateItem(idx, { name: e.target.value })} placeholder="Item name" style={{ fontSize: 12.5, padding: '6px 9px' }} /></div>
                <div><input className="input" value={it.price || ''} onChange={e => updateItem(idx, { price: e.target.value })} placeholder="£0.00" style={{ fontSize: 12.5, padding: '6px 9px', fontFamily: 'var(--font-mono)' }} /></div>
                <div>
                  <select className="input" value={catOf(it)} onChange={e => updateItem(idx, { category: e.target.value })} style={{ fontSize: 12.5, padding: '6px 9px' }}>
                    {catOf(it) === '' && <option value="">— none —</option>}
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <button type="button" className="site-action" onClick={() => setExpanded(expanded === idx ? null : idx)} title="Description">
                    {expanded === idx ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                  </button>
                  <button type="button" className="site-action" onClick={() => removeItem(idx)} title="Remove item"><Trash2 size={13} style={{ color: 'var(--danger)' }} /></button>
                </div>
              </div>
              {expanded === idx && (
                <div style={{ padding: '0 16px 12px 16px' }}>
                  <textarea
                    className="input"
                    value={it.description || ''}
                    onChange={e => updateItem(idx, { description: e.target.value })}
                    placeholder="Description (optional)"
                    rows={2}
                    style={{ fontSize: 12.5, resize: 'vertical', width: '100%' }}
                  />
                </div>
              )}
            </div>
          )
        })}
        <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
          <button type="button" className="btn btn-sm btn-primary" onClick={addItem}><Plus size={13} /> Add menu item</button>
        </div>
      </div>
    </div>
  )
}

// ─── Table list field (inline row-per-item, hours-style) ─────────────────────
// A flat, add/remove list rendered as a clean bordered table with one inline
// input per sub-field — the same look as the Hours/Menu tables. Opt in with
// `variant: 'table'` on a list field. Best for short text/url/image sub-fields
// (e.g. Weekly Locations: day / location / time).

const TableListField = ({ fieldDef, value, onChange, clientId }) => {
  const items = Array.isArray(value) ? value : []
  const cols = (fieldDef.subFields || []).filter(sf => sf.type !== 'multi-image')
  const grid = `${cols.map(sf => (sf.type === 'image' ? '56px' : '1fr')).join(' ')} 56px`

  const addItem = () => {
    const item = {}
    ;(fieldDef.subFields || []).forEach(sf => { item[sf.key] = sf.type === 'multi-image' ? [] : '' })
    onChange([...items, item])
  }
  const updateItem = (idx, key, val) => onChange(items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)))
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx))

  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div className="vars-row vars-header" style={{ gridTemplateColumns: grid }}>
        {cols.map(sf => <div key={sf.key}>{sf.label}</div>)}
        <div></div>
      </div>
      {items.length === 0 && (
        <div style={{ padding: 24, textAlign: 'center', fontSize: 12.5, color: 'var(--text-faint)' }}>Nothing here yet.</div>
      )}
      {items.map((it, idx) => (
        <div className="vars-row" key={idx} style={{ gridTemplateColumns: grid, alignItems: 'center' }}>
          {cols.map(sf => (
            <div key={sf.key}>
              {sf.type === 'image' ? (
                <MenuImageCell value={it[sf.key]} onChange={v => updateItem(idx, sf.key, v)} clientId={clientId} />
              ) : (
                <input
                  className="input"
                  value={it[sf.key] || ''}
                  onChange={e => updateItem(idx, sf.key, e.target.value)}
                  placeholder={sf.placeholder}
                  style={{ fontSize: 12.5, padding: '6px 9px' }}
                />
              )}
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="site-action" onClick={() => removeItem(idx)} title="Remove"><Trash2 size={13} style={{ color: 'var(--danger)' }} /></button>
          </div>
        </div>
      ))}
      <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
        <button type="button" className="btn btn-sm btn-primary" onClick={addItem}><Plus size={13} /> {fieldDef.addLabel || 'Add item'}</button>
      </div>
    </div>
  )
}

export const FieldRenderer = ({ field, value, onChange, clientId }) => {
  switch (field.type) {
    case 'text':
    case 'url':
      return <TextField field={field} value={value} onChange={onChange} />
    case 'textarea':
      return <TextAreaField field={field} value={value} onChange={onChange} />
    case 'image':
      return <ImageField field={field} value={value} onChange={onChange} clientId={clientId} />
    case 'multi-image':
      return <MultiImageField field={field} value={value} onChange={onChange} clientId={clientId} />
    case 'hours':
      return <HoursField value={value} onChange={onChange} />
    case 'list':
      if (field.variant === 'menu')
        return <MenuField value={value} onChange={onChange} clientId={clientId} />
      if (field.variant === 'table')
        return <TableListField fieldDef={field} value={value} onChange={onChange} clientId={clientId} />
      return <ListField fieldDef={field} value={value} onChange={onChange} clientId={clientId} />
    default:
      return null
  }
}

// ─── ContentEditorForm ────────────────────────────────────────────────────────
// Props:
//   siteId          — required, the site to edit
//   clientId        — optional; if provided, fetches /clients/profile?clientId=X (admin context)
//   onBack          — optional; if provided shows a back/close button that calls this
//   showRequestChange — bool (default true); whether to show "Request a Change" button

const ContentEditorForm = ({ siteId, clientId, onBack, showRequestChange = true }) => {
  const queryClient = useQueryClient()

  const [activeTab, setActiveTab] = useState(null)
  const [content,   setContent]   = useState({})
  const [hasUnsaved, setHasUnsaved] = useState(false)

  const { data: site, isLoading: siteLoading } = useQuery(
    ['site', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { enabled: !!siteId }
  )

  // When clientId is provided (admin editing on behalf of a client), fetch that client's profile
  const profileUrl = clientId ? `/clients/profile?clientId=${clientId}` : '/clients/profile'
  const { data: clientProfile, isLoading: profileLoading } = useQuery(
    ['client-profile', clientId || 'self'],
    () => apiService.get(profileUrl).then(r => r.data),
  )

  // Field values live under meta_data.variables — the canonical location the
  // published site renders from (see customDomainRouter.buildTemplateContext)
  // and the same place the desktop editor (NewEditor) reads/writes. Reading the
  // top level here is what made mobile show blank for every field.
  useEffect(() => {
    if (site?.meta_data) {
      try {
        const parsed = typeof site.meta_data === 'string' ? JSON.parse(site.meta_data) : site.meta_data
        setContent(parsed?.variables || {})
      } catch {
        setContent({})
      }
    }
  }, [site])

  // Match the desktop editor's schema source (site.industry) so both show the
  // same fields; fall back to the client's profile industry, then general.
  const industry = site?.industry || clientProfile?.industry || 'general'
  const schema   = INDUSTRIES[industry] || INDUSTRIES.general

  useEffect(() => {
    if (schema && !activeTab) setActiveTab(schema.tabs[0]?.id)
  }, [schema])

  const updateField = useCallback((key, value) => {
    setContent(prev => ({ ...prev, [key]: value }))
    setHasUnsaved(true)
  }, [])

  // Merge the edited fields back into meta_data.variables, preserving the rest
  // of meta_data (brand, CSS/JS, custom code, etc.). Writing the whole object
  // to the top level — the old behaviour — both saved to the wrong place and
  // clobbered those other keys.
  const saveMutation = useMutation(
    (vars) => {
      let meta = {}
      try { meta = typeof site?.meta_data === 'string' ? JSON.parse(site.meta_data) : (site?.meta_data || {}) } catch { meta = {} }
      const merged = { ...meta, variables: vars }
      return apiService.put(`/sites/${siteId}`, { meta_data: JSON.stringify(merged) })
    },
    {
      onSuccess: () => {
        toast.success('Content saved!')
        setHasUnsaved(false)
        queryClient.invalidateQueries(['site', siteId])
      },
      onError: () => toast.error('Failed to save content'),
    }
  )

  const publishMutation = useMutation(
    async () => {
      await saveMutation.mutateAsync(content)
      return apiService.post(`/sites/${siteId}/publish`)
    },
    {
      onSuccess: () => toast.success('Site published!'),
      onError:   () => toast.error('Failed to publish'),
    }
  )

  if (siteLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 256 }}>
        <LoadingSpinner size="lg" text="Loading editor..." />
      </div>
    )
  }

  const activeTabSchema = schema.tabs.find(t => t.id === activeTab)
  // The clientId to use for image uploads — prefer the explicit prop, fall back to the profile's id
  const uploadClientId = clientId || clientProfile?.id

  return (
    <div className="col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
          {onBack && (
            <button
              onClick={onBack}
              className="btn btn-ghost btn-icon btn-sm"
              style={{ flexShrink: 0 }}
            >
              <ArrowLeft size={14} />
            </button>
          )}
          <div style={{ minWidth: 0 }}>
            <h1 className="font-bold truncate" style={{ fontSize: 20, color: 'var(--text)' }}>
              {site?.name || 'Edit Content'}
            </h1>
            <p className="text-muted" style={{ fontSize: 13, marginTop: 2 }}>
              {schema.icon} {schema.label} — update website content below
            </p>
          </div>
          {hasUnsaved && (
            <span style={{ flexShrink: 0, fontSize: 12, color: 'var(--warning)', fontWeight: 500 }}>• Unsaved</span>
          )}
        </div>

        <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
          {showRequestChange && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => window.location.href = '/client/requests/new'}
            >
              <MessageSquare size={13} />
              <span>Request a Change</span>
            </button>
          )}
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => saveMutation.mutate(content)}
            disabled={saveMutation.isLoading}
          >
            {saveMutation.isLoading ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Save
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => publishMutation.mutate()}
            disabled={publishMutation.isLoading}
          >
            {publishMutation.isLoading ? <Loader2 size={13} className="animate-spin" /> : <Globe size={13} />}
            Publish
          </button>
        </div>
      </div>

      {/* Mobile tab bar — only shown ≤768px (the left side-nav replaces it on desktop) */}
      <div className="flex gap-1 editor-tabs-mobile" style={{ overflowX: 'auto', paddingBottom: 4 }}>
        {schema.tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
            style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="flex gap-4">
        {/* Sidebar tabs — desktop only; hidden ≤768px so the mobile tab bar above
            is the single source of navigation (avoids a duplicated menu and frees
            the full width for the form). */}
        <div className="editor-tabs-side" style={{ width: 176, flexShrink: 0 }}>
          <nav className="col gap-1">
            {schema.tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'btn btn-primary btn-sm w-full' : 'btn btn-ghost btn-sm w-full'}
                style={{ textAlign: 'left', justifyContent: 'flex-start' }}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="grow" style={{ minWidth: 0 }}>
          {activeTabSchema && (
            <div className="card">
              <div className="card-pad" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
                <h2 className="font-semibold" style={{ fontSize: 15, color: 'var(--text)' }}>{activeTabSchema.label}</h2>
              </div>
              <div className="card-pad col gap-4">
                {activeTabSchema.fields.map(field => (
                  <FieldRenderer
                    key={field.key}
                    field={field}
                    value={content[field.key]}
                    onChange={val => updateField(field.key, val)}
                    clientId={uploadClientId}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ContentEditorForm
